import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { loadStripeForUser, loadStripeWithRetry } from '../utils/stripe-loader';
import authService from '../services/auth-service';
import PaymentModal from './PaymentModal';
import StripeFallback from './StripeFallback';
import StripeErrorBoundary from './StripeErrorBoundary';
import { getStripeKeyForUser, isStripeTestUser } from '../utils/stripe-config';
import { User } from '../types/auth';
import { TylerState } from '../utils/TylerState';

interface AccountPageProps {
  user: User | null;
  onLogin: (user: User) => void;
  onLogout: () => void;
}

const AccountPage: React.FC<AccountPageProps> = ({ user, onLogin, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'security' | 'subscription' | 'billing' | 'delete' | 'sales' | 'login' | 'register' | 'payment'>(user ? 'overview' : 'sales');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [stripeAvailable, setStripeAvailable] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(true);
  // Form states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  // New states for sales page
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [wantsSupportersClub, setWantsSupportersClub] = useState(false);

  // Dynamic Stripe promise: test users get sandbox key even in production
  const userEmail = user ? user.email : email;
  const stripePromise = useMemo(() => {
    if (userEmail && isStripeTestUser(userEmail)) {
      console.log('STRIPE SANDBOX: Loading test Stripe for user ' + userEmail);
      return loadStripeForUser(userEmail);
    }
    return loadStripeWithRetry();
  }, [userEmail]);

  useEffect(() => {
    // Remove the redirect - we want to show the sales page for guests
    // if (!user) {
    //   navigate("/");
    // }
  }, [user, navigate]);
  // Check Stripe availability
  useEffect(() => {
    const checkStripe = async () => {
      try {
        setStripeLoading(true);
        const stripe = await stripePromise;
        setStripeAvailable(stripe !== null);
      } catch (error) {
        console.error('Stripe check failed:', error);
        setStripeAvailable(false);
      } finally {
        setStripeLoading(false);
      }
    };
    checkStripe();
  }, [stripePromise]);
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      // Initiates Google OAuth redirect (no response object expected)
      authService.googleLogin();
      // After redirect completes, GoogleAuthCallback will dispatch an event to update state
    } catch (err) {
      setError('Google login failed. Please try again.');
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await authService.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully!');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setError('Failed to change password. Please check your current password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(forgotEmail);
      setSuccess('If an account with that email exists, a password reset link has been sent.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    try {
      await authService.resendVerification(user!.email);
      setSuccess('Verification email sent! Check your inbox.');
    } catch (err) {
      setError('Failed to send verification email.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.deleteAccount(deletePassword);
      setSuccess('Account deleted successfully.');
      onLogout();
      navigate('/');
    } catch (err) {
      setError('Failed to delete account. Please check your password.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (email: string) => {
    // After successful payment, wait for backend webhook confirmation
    setEmail(email);
    setLoading(true);
    setSuccess('Payment processing... Please wait while we activate your subscription.');

    // Poll subscription status until webhook completes (max 30 seconds)
    const maxAttempts = 15; // 15 attempts * 2 seconds = 30 seconds
    let attempts = 0;

    const checkSubscriptionStatus = async (): Promise<boolean> => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/payments/subscription-status/${email}`, {
          headers: authService.getAuthHeaders()
        });

        if (!response.ok) {
          console.log('Subscription status check failed:', response.status);
          return false;
        }

        const data = await response.json();
        return data.has_active_subscription === true && data.tier === 'supporter';
      } catch (error) {
        console.error('Error checking subscription status:', error);
        return false;
      }
    };

    while (attempts < maxAttempts) {
      const isActive = await checkSubscriptionStatus();

      if (isActive) {
        // Subscription confirmed! Refresh user data from backend
        try {
          const currentUser = await authService.refreshUserData();
          if (currentUser) {
            setSuccess('✅ Supporters Club subscription activated! Welcome aboard!');
            setActiveTab('overview');
            // Force page reload to refresh user data globally
            setTimeout(() => window.location.reload(), 1500);
            return;
          }
        } catch (error) {
          console.error('Error refreshing user data:', error);
        }
      }

      attempts++;
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before next attempt
      }
    }

    // Timeout - webhook didn't complete in time
    setLoading(false);
    setError('Payment is processing. Your subscription will be activated shortly. Please refresh the page in a minute.');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await authService.login({ email, password });
      const accountUser: User = {
        id: response.user.id,
        email: response.user.email,
        username: response.user.username,
        first_name: response.user.first_name,
        last_name: response.user.last_name,
        is_active: response.user.is_active,
        is_verified: response.user.is_verified || false,
        is_admin: response.user.is_admin || false,
        auth_method: response.user.auth_method || 'email',
        google_id: response.user.google_id,
        created_at: response.user.created_at || new Date().toISOString(),
        // Legacy fields mapped from backend
        createdAt: response.user.created_at || new Date().toISOString(),
        emailVerified: response.user.is_verified || false,
        tier: 'free', // Default tier
        subscriptionEndDate: undefined,
        googleId: response.user.google_id,
        scenarios: []
      };
      onLogin(accountUser);
      setSuccess('Login successful!');
      setActiveTab('overview'); // Go back to overview after login
    } catch (err) {
      // Show the professional error message from the backend
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authService.register({ email, password });
      
      if (wantsSupportersClub) {
        // Go to payment after successful registration
        setActiveTab('payment');
      } else {
        // Show success message for email verification
        setSuccess(response.message || 'Account created! Please check your email for verification.');
        setActiveTab('login'); // Switch to login tab
      }
    } catch (err) {
      // Show the professional error message from the backend
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => {
    const isGoogleAccount = user?.auth_method === 'google';
    const isEmailAccount = user?.auth_method === 'email';
    
    if (!stripeAvailable && !stripeLoading) {
      return (
        <div className="account-page">
          <div className="account-header">
            <Link to="/" className="back-link">← Back to Dashboard</Link>
            <h1>Account Settings</h1>
          </div>
          <StripeFallback onRetry={() => window.location.reload()} />
        </div>
      );
    }

    return (
      <div className="account-section">
        <h2>Account Overview</h2>
        
        <div className="account-info">
          <div className="info-card">
            <h3>Profile</h3>
            <div className="info-item">
              <label>Email:</label>
              <span>{TylerState.user?.email}</span>
            </div>
            <div className="info-item">
              <label>Account Type:</label>
              <span className={`tier-badge ${TylerState.user?.tier}`}>
                {TylerState.user?.tier === 'guest' ? 'Guest' : 
                 user?.tier === 'free' ? 'Free Account' : 'Supporters Club'}
              </span>
            </div>
            <div className="info-item">
              <label>Authentication:</label>
              <span className="auth-method">
                {isGoogleAccount ? '🔒 Google Sign-In' : '📧 Email & Password'}
              </span>
            </div>
            <div className="info-item">
              <label>Member Since:</label>
              <span>{new Date(user?.createdAt || '').toLocaleDateString()}</span>
            </div>
            {/* Only show email verification for email accounts */}
            {isEmailAccount && (
              <div className="info-item">
                <label>Email Verified:</label>
                <span className={user?.emailVerified ? 'verified' : 'unverified'}>
                  {user?.emailVerified ? '✓ Verified' : '✗ Unverified'}
                </span>
              </div>
            )}
            {isGoogleAccount && (
              <div className="info-item">
                <label>Email Status:</label>
                <span className="verified">✓ Verified by Google</span>
              </div>
            )}
          </div>

          {/* Only show verification notice for unverified email accounts */}
          {isEmailAccount && !user?.emailVerified && (
            <div className="verification-notice">
              <p>Please verify your email address to access all features.</p>
              <button onClick={handleResendVerification} className="btn-secondary">
                Resend Verification Email
              </button>
            </div>
          )}

        {TylerState.user?.tier === 'free' && (
          <div className="upgrade-notice">
            <h3>Upgrade to Supporters Club</h3>
            <p>Get unlimited scenarios and PDF export for advisors.</p>
            <button onClick={() => setShowPaymentModal(true)} className="btn-primary">
              Upgrade Now - $10/year
            </button>
          </div>
        )}

        {TylerState.user?.tier === 'supporter' && user?.subscriptionEndDate && (
          <div className="subscription-info">
            <h3>Supporters Club</h3>
            <p>Your subscription is active until {new Date(user.subscriptionEndDate).toLocaleDateString()}</p>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderSecurity = () => {
    const isGoogleAccount = user?.auth_method === 'google';
    const isEmailAccount = user?.auth_method === 'email';
    
    if (!stripeAvailable && !stripeLoading) {
      return (
        <div className="account-page">
          <div className="account-header">
            <Link to="/" className="back-link">← Back to Dashboard</Link>
            <h1>Account Settings</h1>
          </div>
          <StripeFallback onRetry={() => window.location.reload()} />
        </div>
      );
    }

    return (
      <div className="account-section">
        <h2>Security Settings</h2>
        
        <div className="security-options">
          {/* Password Management - Only for email accounts */}
          {isEmailAccount && (
            <>
              <div className="option-card">
                <h3>Change Password</h3>
                <p>Update your account password</p>
                <button onClick={() => setShowPasswordChange(true)} className="btn-secondary">
                  Change Password
                </button>
              </div>

              <div className="option-card">
                <h3>Forgot Password</h3>
                <p>Send password reset link to your email</p>
                <button onClick={() => setShowForgotPassword(true)} className="btn-secondary">
                  Send Reset Link
                </button>
              </div>
            </>
          )}

          {/* Google Account Management */}
          <div className="option-card">
            <h3>Google Account</h3>
            {isGoogleAccount ? (
              <>
                <p>✅ Your account is linked to Google</p>
                <p className="text-muted">You sign in using Google OAuth. No password needed.</p>
                <button onClick={() => {}} className="btn-secondary" disabled>
                  Unlink Google (Coming Soon)
                </button>
              </>
            ) : (
              <>
                <p>Link your Google account for easier sign-in</p>
                <button onClick={handleGoogleLogin} className="btn-secondary">
                  Link Google Account
                </button>
              </>
            )}
          </div>

          {/* Account Type Info */}
          <div className="option-card info-only">
            <h3>Authentication Method</h3>
            <p>
              {isGoogleAccount ? (
                <>
                  <strong>Google Sign-In:</strong> You authenticate using your Google account. 
                  This is secure and convenient - no passwords to remember!
                </>
              ) : (
                <>
                  <strong>Email & Password:</strong> You authenticate using your email and password. 
                  You can change your password or reset it if forgotten.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderSubscription = () => (
    <div className="account-section">
      <h2>Subscription Management</h2>
      
      <div className="subscription-details">
        <div className="subscription-card">
          <h3>Current Plan</h3>
          <div className="plan-info">
            <span className={`plan-badge ${TylerState.user?.tier}`}>
              {TylerState.user?.tier === 'guest' ? 'Guest' : 
               user?.tier === 'free' ? 'Free Account' : 'Supporters Club'}
            </span>
            {TylerState.user?.tier === 'supporter' && user?.subscriptionEndDate && (
              <p>Renews on {new Date(user.subscriptionEndDate).toLocaleDateString()}</p>
            )}
          </div>
        </div>

        {TylerState.user?.tier === 'free' && (
          <div className="upgrade-card">
            <h3>Upgrade to Supporters Club</h3>
            <ul>
              <li>✓ Unlimited scenarios</li>
              <li>✓ PDF export for advisors</li>
              <li>✓ Priority support</li>
            </ul>
            <button onClick={() => setShowPaymentModal(true)} className="btn-primary">
              Upgrade Now - $10/year
            </button>
          </div>
        )}

        {TylerState.user?.tier === 'supporter' && user?.subscriptionEndDate && (
          <div className="subscription-info">
            <h3>✓ Active Supporter</h3>
            <p>Your Supporters Club membership is active until <strong>{new Date(user.subscriptionEndDate).toLocaleDateString()}</strong></p>
            <p className="no-auto-renewal-note">
              ℹ️ Your membership will not auto-renew. We'll send you a reminder email before it expires, and you can choose whether to renew.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderBilling = () => (
    <div className="account-section">
      <h2>Billing History</h2>

      <div className="billing-history">
        {TylerState.user?.tier === 'supporter' && user?.subscriptionEndDate ? (
          <div className="billing-record">
            <h4>Supporters Club Membership</h4>
            <div className="billing-details">
              <div className="billing-item">
                <label>Amount Paid:</label>
                <span>$10.00 CAD</span>
              </div>
              <div className="billing-item">
                <label>Valid Until:</label>
                <span>{new Date(user.subscriptionEndDate).toLocaleDateString()}</span>
              </div>
              <div className="billing-item">
                <label>Auto-Renewal:</label>
                <span>No - You control renewals</span>
              </div>
            </div>
            <p className="billing-note">
              📧 We'll email you 30 days before your membership expires with renewal options.
            </p>
          </div>
        ) : (
          <div className="no-billing">
            <p>You haven't made any payments yet.</p>
            <p>Upgrade to Supporters Club for unlimited scenarios, bookkeeping for up to 10 businesses, and scheduling Pro features!</p>
            <button onClick={() => setShowPaymentModal(true)} className="btn-primary">
              Upgrade Now - $10/year
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderDelete = () => (
    <div className="account-section">
      <h2>Delete Account</h2>
      
      <div className="delete-warning">
        <h3>⚠️ Warning</h3>
        <p>This action cannot be undone. All your data will be permanently deleted.</p>
        <ul>
          <li>All saved scenarios will be lost</li>
          <li>Your account will be permanently removed</li>
          <li>Any active subscriptions will be cancelled</li>
        </ul>
        <button 
          onClick={() => setShowDeleteConfirm(true)} 
          className="btn-danger"
        >
          Delete My Account
        </button>
      </div>
    </div>
  );

  const renderSalesPage = () => (
    <div className="sales-page">
      <div className="sales-header">
        <Link to="/" className="back-link">← Back to Dashboard</Link>
        <h1>Larry's Help</h1>
        <p className="sales-subtitle">Easy to use financial planning tools for everyone</p>
      </div>

      <div className="sales-signin">
         <p>Already have an account? <button onClick={() => setActiveTab('login')} className="link-button">Sign In</button></p>
         <div className="sales-google-signin">          
          <button 
            onClick={handleGoogleLogin} 
            className="google-signin-button sales-google-button"
            disabled={loading}
          >
            {loading ? (
              <span className="loading-spinner">⏳</span>
            ) : (
              <>
                <img 
                  src="https://developers.google.com/identity/images/g-logo.png" 
                  alt="Google" 
                  className="google-icon"
                />
                Sign in with Google
              </>
            )}
          </button>
        </div>
       </div>

       <div className="tier-comparison">
        <div className="tier-card guest-tier">
          <div className="tier-header">
            <h3>Guest Mode</h3>
          </div>
          <div className="tier-features">
            <div className="feature">
              <span>Run single scenarios without an account</span>
            </div>
          </div>
          <Link to="/" className="tier-button guest-button">
            Continue as Guest
          </Link>
        </div>

        <div className="tier-card free-tier">
          <div className="tier-header">
            <h3>Create an Account</h3>
          </div>
          <div className="tier-features">
            <div className="feature">
              <span>Save scenarios for free!</span>
            </div>
          </div>
          <button className="tier-button free-button" onClick={() => setActiveTab('register')}>
            Create Free Account
          </button>
        </div>

        <div className="tier-card supporter-tier">
          <div className="tier-header">
            <h3>Supporters Club</h3>
            <p className="tier-subtitle" style={{fontWeight: 'bold'}}>⭐ MOST POPULAR ⭐</p>
          </div>
          <div className="tier-price">$10 for annual subscription</div>
          <div className="tier-features">
            <div className="feature">
              <span className="feature-icon">∞</span>
              <span>Unlimited scenarios</span>
            </div>
            <div className="feature">
              <span className="feature-icon">📄</span>
              <span>PDF export for advisors</span>
            </div>
          </div>
          <button 
            className="tier-button supporter-button" 
            onClick={() => {
              setWantsSupportersClub(true);
              setActiveTab('register');
            }}
          >
            Join Supporters Club
          </button>
        </div>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="auth-form">
      <div className="auth-header">
        <Link to="/" className="back-link">← Back to Dashboard</Link>
        <h2>Sign In</h2>
        <p>Welcome back to Larry's Help</p>
      </div>

      <form onSubmit={handleLogin}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button 
            type="button" 
            onClick={() => setShowForgotPassword(true)} 
            className="forgot-password-link"
          >
            Forgot Password?
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="auth-button" disabled={loading}>
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>

      <div className="auth-footer">
        <p>Don't have an account? <button onClick={() => setActiveTab('register')} className="link-button">Sign Up</button></p>
        <button onClick={() => setActiveTab('sales')} className="link-button">Back to Plans</button>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="auth-form">
      <div className="auth-header">
        <Link to="/" className="back-link">← Back to Dashboard</Link>
        <h2>{wantsSupportersClub ? 'Create Supporters Account' : 'Create Account'}</h2>
        <p>{wantsSupportersClub ? 'Join Larry\'s Help Supporters Club' : 'Join Larry\'s Help'}</p>
      </div>

      <form onSubmit={handleRegister}>
        <div className="form-group">
          <label htmlFor="reg-email">Email</label>
          <input
            type="email"
            id="reg-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="reg-password">Password</label>
          <input
            type="password"
            id="reg-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirm-password">Confirm Password</label>
          <input
            type="password"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        {wantsSupportersClub && (
          <div className="info-message">
            <p>After creating your account, you'll be taken to complete your Supporters Club payment.</p>
          </div>
        )}

        <button type="submit" className="auth-button" disabled={loading}>
          {loading ? (wantsSupportersClub ? 'Creating Account...' : 'Creating Account...') : (wantsSupportersClub ? 'Create Account & Continue to Payment' : 'Create Account')}
        </button>
      </form>

      <div className="auth-footer">
        <p>Already have an account? <button onClick={() => setActiveTab('login')} className="link-button">Sign In</button></p>
        <button onClick={() => setActiveTab('sales')} className="link-button">Back to Plans</button>
      </div>
    </div>
  );

  if (!user) {
    if (!stripeAvailable && !stripeLoading) {
      return (
        <div className="account-page">
          <div className="account-header">
            <Link to="/" className="back-link">← Back to Dashboard</Link>
            <h1>Account</h1>
          </div>
          <StripeFallback onRetry={() => window.location.reload()} />
        </div>
      );
    }

    return (
      <StripeErrorBoundary>
        <Elements stripe={stripePromise}>
          <div className="account-page">
            {activeTab === 'sales' && renderSalesPage()}
            {activeTab === 'login' && renderLoginForm()}
            {activeTab === 'register' && renderRegisterForm()}
            {activeTab === 'payment' && (
              <PaymentModal
                isOpen={true}
                onClose={() => setActiveTab('register')}
                onSuccess={handlePaymentSuccess}
                email={email}
              />
            )}
          </div>
        </Elements>
      </StripeErrorBoundary>
    );
  }

  if (!stripeAvailable && !stripeLoading) {
      return (
        <div className="account-page">
          <div className="account-header">
            <Link to="/" className="back-link">← Back to Dashboard</Link>
            <h1>Account Settings</h1>
          </div>
          <StripeFallback onRetry={() => window.location.reload()} />
        </div>
      );
    }

    return (
    <StripeErrorBoundary><Elements stripe={stripePromise}>
      <div className="account-page">
        <div className="account-header">
          <Link to="/" className="back-link">← Back to Dashboard</Link>
          <h1>Account Settings</h1>
        </div>

        <div className="account-container">
          <div className="account-sidebar">
            <nav className="account-nav">
              <button 
                className={`nav-button ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button 
                className={`nav-button ${activeTab === 'security' ? 'active' : ''}`}
                onClick={() => setActiveTab('security')}
              >
                Security
              </button>
              <button 
                className={`nav-button ${activeTab === 'subscription' ? 'active' : ''}`}
                onClick={() => setActiveTab('subscription')}
              >
                Subscription
              </button>
              <button 
                className={`nav-button ${activeTab === 'billing' ? 'active' : ''}`}
                onClick={() => setActiveTab('billing')}
              >
                Billing
              </button>
              <button 
                className={`nav-button ${activeTab === 'delete' ? 'active' : ''}`}
                onClick={() => setActiveTab('delete')}
              >
                Delete Account
              </button>
            </nav>
          </div>

          <div style={{padding: '30px'}}>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'security' && renderSecurity()}
            {activeTab === 'subscription' && renderSubscription()}
            {activeTab === 'billing' && renderBilling()}
            {activeTab === 'delete' && renderDelete()}
          </div>
        </div>

        {/* Modals */}
        {showPaymentModal && (
          <PaymentModal
            isOpen={true}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={handlePaymentSuccess}
            email={user.email}
          />
        )}

        {showPasswordChange && (
          <div className="modal-overlay" onClick={() => setShowPasswordChange(false)}>
            <div className="modal-content welcome-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Change Password</h3>
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowPasswordChange(false)}>
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}>
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showForgotPassword && (
          <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
            <div className="modal-content welcome-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Reset Password</h3>
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowForgotPassword(false)}>
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Reset Email'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal-content welcome-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Account Deletion</h3>
              <p>This action cannot be undone. All your scenarios and data will be permanently deleted.</p>
              <form onSubmit={handleDeleteAccount}>
                {user?.auth_method === 'email' && (
                  <div className="form-group">
                    <label>Enter your password to confirm:</label>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      required
                      placeholder="Your password"
                    />
                  </div>
                )}
                {user?.auth_method === 'google' && (
                  <div className="delete-confirmation-notice">
                    <p><strong>⚠️ This will delete:</strong></p>
                    <ul>
                      <li>All your saved scenarios</li>
                      <li>Your account data</li>
                      <li>Any active Supporters Club membership</li>
                    </ul>
                    <p>Are you sure you want to proceed?</p>
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="btn-danger">
                    {loading ? 'Deleting...' : 'Delete My Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Elements></StripeErrorBoundary>
  );
};

export default AccountPage; 